/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { loadCompositionEstimates } from "components/jiffy/products/product-composition-estimates";
import CompositionEstimatesClass from "components/jiffy/products/composition-estimates-class";
import initProductCardV3 from "components/jiffy/product-card-v3/product-card-v3";
import { isElementVisible } from "common/utils/ui/visibility";
import { CachedDeliveryManager } from "common/utils/delivery-date/cached-delivery-manager";
import EventBus from "common/eventbus";
import Events from "common/events";

const OBSERVER_ROOT_MARGIN = "100px";
const OBSERVER_THRESHOLD = 0.1;
const PRODUCT_CARD_SELECTOR = ".js-product-card-v3";

/**
 * Manages product cards in a grid layout with lazy loading functionality.
 * Handles intersection observation to load composition estimates as cards become visible.
 */
class ProductsGrid {
  #productCardSelector = PRODUCT_CARD_SELECTOR;

  #processedIds;

  #observer;

  #beforeUnloadHandler;

  #gridOptions;

  #zipcodeUpdateHandler;

  /**
   * Creates a new ProductsGridClass instance.
   * @param {string} productCardSelector - Selector for product cards
   */
  constructor(productCardSelector = PRODUCT_CARD_SELECTOR) {
    this.#processedIds = new Set();
    this.#observer = null;
    this.#beforeUnloadHandler = null;
    this.#zipcodeUpdateHandler = null;
    this.#gridOptions = null;
    this.#productCardSelector = productCardSelector;

    // Check for made category
    const $container = document.querySelector(".js-made-category");
    if (!$container) return;
    const { isMadeCategoryPage } = $container.dataset;

    this.#gridOptions = {
      isMade: isMadeCategoryPage,
      isProductCardV3: true,
      isSendIndividually: !CompositionEstimatesClass.shouldUseFastEta("Print")
    };
  }

  /**
   * Gets the Set of processed composition IDs.
   * @returns {Set<string>} Set of composition IDs that have been processed
   */
  get processedIds() {
    return this.#processedIds;
  }

  /**
   * Gets the NodeList of product cards.
   * @returns {NodeListOf<Element>} NodeList of product card elements
   */
  get #$cards() {
    return document.querySelectorAll(this.#productCardSelector);
  }

  /**
   * Initializes the product grid by setting up cards, loading visible compositions,
   * and configuring lazy loading.
   */
  initialize() {
    if (!this.#$cards.length) return;

    initProductCardV3(this.#$cards);

    this.#loadVisibleCompositions();
    this.#setupLazyLoading();

    this.#beforeUnloadHandler = () => this.destroy();
    window.addEventListener("beforeunload", this.#beforeUnloadHandler);

    this.#zipcodeUpdateHandler = () => this.#handleZipcodeUpdate();
    EventBus.on(Events.ZIPCODE_UPDATED, this.#zipcodeUpdateHandler);
  }

  /**
   * Handles zipcode update event to refresh delivery dates on cards.
   * @private
   */
  #handleZipcodeUpdate() {
    this.#processedIds.clear();

    if (window.jiffyCachedDeliveryData?.deliveryDate) {
      this.#$cards.forEach($card => {
        if (ProductsGrid.#shouldUpdateCard($card)) {
          ProductsGrid.#applyCachedDeliveryDate($card);

          const { compositionId } = $card.dataset;
          if (compositionId) {
            this.#processedIds.add(compositionId);
          }
        }
      });
    } else {
      this.#$cards.forEach($card => {
        if (isElementVisible($card) && ProductsGrid.#shouldUpdateCard($card)) {
          const $loadingEl = $card.querySelector(".js-plp-delivery-loading");
          const $loadedEl = $card.querySelector(".js-plp-delivery-loaded");

          if ($loadingEl) {
            $loadingEl.classList.remove("hidden");
          }
          if ($loadedEl) {
            $loadedEl.classList.add("hidden");
            $loadedEl.classList.remove("flex");
          }
        }
      });

      this.#observeUnprocessedCards();
      this.#loadVisibleCompositions();
    }
  }

  /**
   * Checks if a card should be updated with cached delivery date.
   * @param {Element} $card - The product card element
   * @returns {boolean} - True if the card should be updated
   * @private
   */
  static #shouldUpdateCard($card) {
    return (
      $card.dataset.hasCachedDelivery === "true" ||
      $card.querySelector('[data-has-cached-delivery="true"]') ||
      $card.querySelector(".js-plp-delivery-date")
    );
  }

  /**
   * Loads composition estimates for visible cards that haven't been processed yet.
   * @private
   */
  #loadVisibleCompositions() {
    const { visibleIds: compositionIds, businessLineMap } = this.#getVisibleCompositionIds();
    if (compositionIds.length) {
      if (this.#gridOptions?.isMade) {
        compositionIds.forEach(compositionId => {
          const options = { ...this.#gridOptions, businessLine: businessLineMap[compositionId] };
          loadCompositionEstimates([compositionId], options).catch(error => {
            console.error(error);
          });
        });
      } else {
        const groups = ProductsGrid.#groupByBusinessLine(compositionIds, businessLineMap);
        Object.entries(groups).forEach(([bl, ids]) => {
          const options = { ...this.#gridOptions, businessLine: bl };
          loadCompositionEstimates(ids, options).catch(error => {
            console.error(error);
          });
        });
      }
    }
  }

  /**
   * Gets composition IDs of visible cards that haven't been processed yet.
   * @returns {string[]} Array of composition IDs
   * @private
   */
  #getVisibleCompositionIds() {
    const visibleIds = [];
    const businessLineMap = {};

    this.#$cards.forEach($card => {
      if (isElementVisible($card)) {
        const { compositionId } = $card.dataset;
        const businessLine = ProductsGrid.#resolveBusinessLine($card);

        if (compositionId && !this.#processedIds.has(compositionId)) {
          const hasCachedDelivery =
            $card.dataset.hasCachedDelivery === "true" ||
            $card.querySelector('[data-has-cached-delivery="true"]');

          if (hasCachedDelivery && window.jiffyCachedDeliveryData?.deliveryDate) {
            this.#processedIds.add(compositionId);
            ProductsGrid.#applyCachedDeliveryDate($card);
          } else if ($card.querySelector(".js-plp-delivery-date")) {
            this.#processedIds.add(compositionId);
            visibleIds.push(compositionId);
            if (businessLine) {
              businessLineMap[compositionId] = businessLine;
            }
          }
        }
      }
    });

    return { visibleIds, businessLineMap };
  }

  /**
   * Sets up IntersectionObserver for lazy loading cards as they scroll into view.
   * @private
   */
  #setupLazyLoading() {
    const observerOptions = {
      root: null,
      rootMargin: OBSERVER_ROOT_MARGIN,
      threshold: OBSERVER_THRESHOLD
    };

    this.#observer = new IntersectionObserver(this.#handleIntersection.bind(this), observerOptions);

    this.#observeUnprocessedCards();
  }

  /**
   * Starts observing unprocessed cards for intersection.
   * @private
   */
  #observeUnprocessedCards() {
    this.#$cards.forEach($card => {
      const { compositionId } = $card.dataset;
      if (compositionId && !this.#processedIds.has(compositionId)) {
        this.#observer.observe($card);
      }
    });
  }

  /**
   * Handles intersection events when cards become visible.
   * @param {IntersectionObserverEntry[]} entries - Array of intersection observer entries
   * @private
   */
  #handleIntersection($entries) {
    const compositionIdsToLoad = [];
    const businessLineMap = {};

    $entries.forEach($entry => {
      if ($entry.isIntersecting) {
        const { compositionId } = $entry.target.dataset;
        const businessLine = ProductsGrid.#resolveBusinessLine($entry.target);

        if (compositionId && !this.#processedIds.has(compositionId)) {
          const hasCachedDelivery =
            $entry.target.dataset.hasCachedDelivery === "true" ||
            $entry.target.querySelector('[data-has-cached-delivery="true"]');

          if (hasCachedDelivery && window.jiffyCachedDeliveryData?.deliveryDate) {
            this.#processedIds.add(compositionId);
            ProductsGrid.#applyCachedDeliveryDate($entry.target);
          } else if ($entry.target.querySelector(".js-plp-delivery-date")) {
            compositionIdsToLoad.push(compositionId);
            this.#processedIds.add(compositionId);
            if (businessLine) {
              businessLineMap[compositionId] = businessLine;
            }
          }

          this.#observer.unobserve($entry.target);
        }
      }
    });

    if (compositionIdsToLoad.length) {
      if (this.#gridOptions?.isMade) {
        compositionIdsToLoad.forEach(compositionId => {
          const options = { ...this.#gridOptions, businessLine: businessLineMap[compositionId] };
          loadCompositionEstimates([compositionId], options).catch(error => {
            console.error(error);
          });
        });
      } else {
        const groups = ProductsGrid.#groupByBusinessLine(compositionIdsToLoad, businessLineMap);
        Object.entries(groups).forEach(([bl, ids]) => {
          const options = { ...this.#gridOptions, businessLine: bl };
          loadCompositionEstimates(ids, options).catch(error => {
            console.error(error);
          });
        });
      }
    }
  }

  /**
   * Applies cached delivery date to a product card.
   * @param {Element} $card - The product card element
   * @private
   */
  static #resolveBusinessLine($el) {
    const { businessLine, customizable } = $el.dataset;
    return customizable === "true" ? "Print" : businessLine;
  }

  static #groupByBusinessLine(compositionIds, businessLineMap) {
    const groups = {};
    compositionIds.forEach(id => {
      const bl = businessLineMap[id] || "";
      if (!groups[bl]) groups[bl] = [];
      groups[bl].push(id);
    });
    return groups;
  }

  static #applyCachedDeliveryDate($card) {
    const $deliveryDateElements = $card.querySelectorAll(".js-plp-delivery-date");

    const setLoadingState = isLoading => {
      const $loadingEl = $card.querySelector(".js-plp-delivery-loading");
      const $loadedEl = $card.querySelector(".js-plp-delivery-loaded");

      if ($loadingEl) {
        $loadingEl.classList.toggle("hidden", !isLoading);
      }
      if ($loadedEl) {
        $loadedEl.classList.toggle("hidden", isLoading);
        $loadedEl.classList.toggle("flex", !isLoading);
      }
    };

    CachedDeliveryManager.applyCachedDelivery({
      deliveryDateElements: $deliveryDateElements,
      setLoadingState
    });

    const $deliveryTimeleftTextEl = $card.querySelector(".js-plp-delivery-timeleft-text");
    if ($deliveryTimeleftTextEl) {
      CachedDeliveryManager.applyCachedTimeLeftText(
        $deliveryTimeleftTextEl,
        window.jiffyCachedDeliveryData?.hasDeliveryStartEnd,
        window.jiffyCachedDeliveryData?.cutoffLabel
      );
    }
  }

  /**
   * Cleans up resources by disconnecting observer and clearing data.
   */
  destroy() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
    this.#processedIds.clear();
    this.#gridOptions = null;

    if (this.#beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.#beforeUnloadHandler);
      this.#beforeUnloadHandler = null;
    }

    if (this.#zipcodeUpdateHandler) {
      EventBus.removeEventListener(Events.ZIPCODE_UPDATED, this.#zipcodeUpdateHandler);
      this.#zipcodeUpdateHandler = null;
    }
  }
}

export default () => new ProductsGrid().initialize();
